/** ReactiveFormService
 *
 * > documentation : apps/shared/doc/reactive-form.md
 *
 * - do not forget to bind the formGroup with the service
 * - listen submit from service - not from dom
 */
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NonNullableFormBuilder,
  ValidatorFn,
} from '@angular/forms';
import type { Subscription } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  retry,
  skip,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';

import type { HttpError, ValidationError } from '@evc/platform';
import { ErrorTypes, HttpCode } from '@evc/platform';
import type { Maybe } from '@evc/web-components';
import { ToastService } from '@evc/web-components';

import { I18nService } from '../services/i18n/i18n.service';
import type {
  FormServiceBindindOptions,
  FormServiceSubmitHook,
  OnError,
  OnSuccessWithoutSubmitHook,
  OnSuccessWithSubmitHook,
} from './types/reactive-form.type';
import type { EvcValidator } from './types/validator.type';
import { TypedError } from './utils/typed-error';
import { EvcValidatorService } from './validator/validator.service';

type SubmitNextPayloadWithHook<U> = [U];
type SubmitNextPayloadWithoutHook<T> = [Partial<T>, T];
type SubmitNextPayload<T, U> = SubmitNextPayloadWithoutHook<T> | SubmitNextPayloadWithHook<U>;

@Injectable()
export class EvcFormService<T extends Record<string, unknown>, U = void> {
  /** usefull to hide error messages until submited - reset on success */
  submited = signal(false);

  readonly #validatorService = inject(EvcValidatorService);
  readonly #i18nService = inject(I18nService);
  readonly #toastService = inject(ToastService);

  #formGroup!: FormGroup;
  #formDirective!: FormGroupDirective;
  #submitHook?: Maybe<FormServiceSubmitHook<T, U>>;
  #catchErrors = true;

  // because this service is tight to the form component - like to takeUntilDestroyed form vs service
  #formDestroyRef: Maybe<DestroyRef> = undefined;
  #serviceDestroyRef = inject(DestroyRef);
  get #destroyRef(): DestroyRef {
    return this.#formDestroyRef ?? this.#serviceDestroyRef;
  }

  /** important - you MUST bind some infos with this service
   * will link form directive to optimized service observables (onSubmit$, onUpdate$)
   */
  bind({
    formGroup,
    formDirective,
    destroyRef,
    submitHook,
    onUpdate,
    onSuccess,
    onError,
    catchErrors,
  }: FormServiceBindindOptions<T, U>): void {
    this.#formGroup = formGroup;
    this.#formDirective = formDirective;
    this.#formDestroyRef = destroyRef;
    this.#submitHook = submitHook;
    this.#catchErrors = catchErrors ?? true;

    this.#bindOnSubmit();
    this.#bindOnUpdate();

    if (onUpdate) this.onUpdate(onUpdate);

    if (onSuccess || onError) {
      if (submitHook) {
        this.onSubmit((onSuccess as OnSuccessWithSubmitHook<U>) ?? (() => undefined), onError);
      } else {
        this.onSubmit((onSuccess as OnSuccessWithoutSubmitHook<T>) ?? (() => undefined), onError);
      }
    }
  }

  /** trigger form submit */
  submit(event?: Event): void {
    event?.preventDefault?.();

    return this.#formDirective.ngSubmit.next(undefined);
  }

  onUpdate(callback: (data: T) => void): Subscription {
    return this.onUpdate$.pipe(tap(callback)).subscribe();
  }

  /** without submitHook, expect form data  */
  /** with submitHook, expect api response  */
  onSubmit(
    onSuccess: OnSuccessWithoutSubmitHook<T> | OnSuccessWithSubmitHook<U>,
    onError?: OnError,
  ): Subscription {
    return this.onSubmit$
      .pipe(
        catchError((error, caught) => {
          onError?.(error);

          return caught.pipe(retry());
        }),
        tap(
          ([
            dataOrResponse,
            maybeRawData,
          ]: SubmitNextPayload<T, U>) => {
            if (!maybeRawData) {
              return (onSuccess as OnSuccessWithSubmitHook<U>)(dataOrResponse as U);
            }

            return (onSuccess as OnSuccessWithoutSubmitHook<T>)(
              dataOrResponse as Partial<T>,
              maybeRawData,
            );
          },
        ),
      )
      .subscribe();
  }

  /** easy control factory */
  /** Two separate factory methods to handle different control types */
  controlFactory(
    formBuilder: FormBuilder | NonNullableFormBuilder,
    model: T,
  ): (
    key: keyof typeof model,
    validators?: (EvcValidator | ValidatorFn)[],
    required?: boolean,
  ) => FormControl {
    const validator = this.#validatorService;

    return function control(
      key: keyof typeof model,
      validators: (EvcValidator | ValidatorFn)[] = [],
      required = true,
    ) {
      const validatorsWithDefaults = [...validators];
      if (required) validatorsWithDefaults.push(validator.required());

      let value = model[key];
      if (typeof value === 'string') value = (value ?? '') as T[keyof T];

      return formBuilder.control(value, validatorsWithDefaults);
    };
  }

  arrayControlFactory(
    formBuilder: FormBuilder | NonNullableFormBuilder,
    model: T,
  ): (
    key: keyof typeof model,
    validators?: (EvcValidator | ValidatorFn)[],
    required?: boolean,
  ) => FormArray {
    const validator = this.#validatorService;

    return function arrayControl(
      key: keyof typeof model,
      validators: (EvcValidator | ValidatorFn)[] = [],
      required = false,
    ) {
      const validatorsWithDefaults = [...validators];
      if (required) validatorsWithDefaults.push(validator.required());

      const value = Array.isArray(model[key]) ? (model[key] as never[]) : [];

      return formBuilder.array(value as never[], validatorsWithDefaults);
    };
  }

  // // `private` instead of `#` because of test spy
  private _onUpdate$ = new Subject<T>();
  get onUpdate$(): Observable<T> {
    return this._onUpdate$.asObservable().pipe(takeUntilDestroyed(this.#destroyRef));
  }

  /* So our service emits a onUpdate$ when detect changes */
  #bindOnUpdate(): void {
    this.#formGroup.valueChanges
      .pipe(
        skip(1),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
        tap((value: T) => this._onUpdate$.next(value)),
      )
      .subscribe();
  }

  // // `private` because of test spy
  private _onSubmit$ = new Subject<{ next?: SubmitNextPayload<T, U>; error?: TypedError }>();
  /**
   * IMPORTANT to pipe with retry()
   * or simply use this.onSubmit()
   *
   * this.#formService.onSubmit$
   *   .pipe( catchError((error, caught) => {
   *     return caught.pipe(retry()); // important or stop on error
   *   }) ).subscribe((data) => { });
   **/
  get onSubmit$(): Observable<SubmitNextPayload<T, U>> {
    return this._onSubmit$.asObservable().pipe(
      takeUntilDestroyed(this.#destroyRef),
      map(({ next, error }) => {
        if (error) throw error;
        else return next!;
      }),
      tap(this.#clearState.bind(this)),
    );
  }

  #bindOnSubmit(): void {
    const ngSubmit$ = (() => {
      if (this.#formDestroyRef instanceof Observable) {
        return this.#formDirective.ngSubmit.pipe(takeUntil(this.#formDestroyRef));
      }

      return this.#formDirective.ngSubmit.pipe(takeUntilDestroyed(this.#destroyRef));
    })();

    ngSubmit$
      .pipe(
        filter((event: SubmitEvent) => {
          const submitter = event?.submitter as HTMLButtonElement | null;

          return !submitter || submitter?.getAttribute('formGroupSubmit') === 'true';
        }),
        tap(() => this.submited.set(true)),
        map(
          () =>
            [
              this.#formGroup.value,
              this.#formGroup.getRawValue(),
            ] as SubmitNextPayloadWithoutHook<T>,
        ),
        tap(([value, rawValue]) => this.#onSubmit(value, rawValue)),
      )
      .subscribe();
  }

  /** We catch the form Submission here
   * - maybe chain an API call
   * - handle validation error (both front and back)
   * then use _onSubmit$ to trigger service's onSubmit(onSuccess, onError)
   */
  #onSubmit(value: Partial<T>, rawValue: T): void {
    /* Frontend Validation Error */
    if (!this.#formGroup.valid) {
      const invalidError = {
        error: this.#formGroup.errors,
        status: HttpCode.BadRequest,
      } as HttpError;

      return this.#triggerNextError(new TypedError(invalidError));
    }

    /* Frontend Validation Success - No Api Call */
    if (!this.#submitHook) return this.#triggerNextSuccess(value, rawValue);

    /* chain API Call */
    this.#submitHook(value, rawValue)
      .pipe(
        /* Backend API Success */
        tap((response) => this.#triggerNextSuccess(response)),
        /* Backend API Error */
        catchError(this.#catchApiErrors.bind(this)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  /**
   * next may be :
   * - without submit hook : after validation front - so value is form values
   * - with submit hook : after submit success - so value is api response
   *  */
  #triggerNextSuccess(value: Partial<T>, rawValue: T): void;
  #triggerNextSuccess(response: U): void;
  #triggerNextSuccess(valueOrResponse: Partial<T> | U, rawValue?: T): void {
    const payload = rawValue
      ? ([valueOrResponse, rawValue] as SubmitNextPayloadWithoutHook<T>)
      : ([valueOrResponse] as SubmitNextPayloadWithHook<U>);

    this._onSubmit$.next({ next: payload });
  }

  #triggerNextError(error: TypedError): void {
    return this._onSubmit$.next({ error });
  }

  /** catch api errors
   * - if ValidationErrors > resync them to form controls
   * - else > generic error (Internal | Unprocessable | Unknown.. are all generic failure for our user)
   * then trigger submit$ failure
   */

  #catchApiErrors(httpError: HttpError): Observable<false> {
    const error = new TypedError(httpError);
    if (this.#catchErrors) {
      if (error.type === ErrorTypes.INVALID) {
        const errors = httpError.error.errors as ValidationError[];
        this.#syncApiValidationErrorsToFormControls(errors as ValidationError[]);
      } else {
        this.#showGenericErrorToast(error);
      }
    }
    this.#triggerNextError(error);

    return of(false);
  }

  /** assign api errors to corresponding form controls */
  #syncApiValidationErrorsToFormControls(errors: ValidationError[]) {
    function groupByControl(
      stack: Record<string, Record<string, { message: string }>>,
      error: ValidationError,
    ) {
      const { propertyName: control, message, type } = error;
      if (!stack[control]) stack[control] = {};

      stack[control][type] = { message };

      return stack;
    }

    Object.entries(errors.reduce(groupByControl, {})).forEach(([propertyName, errorMap]) => {
      this.#formGroup.get(propertyName)?.setErrors(errorMap);
    });
  }

  /** show generic toast error message */
  #showGenericErrorToast(error: TypedError) {
    const message = (() => {
      if (error.type === ErrorTypes.UNPROCESSABLE && error.message) {
        return error.message;
      }
      const errorKey = `errors.${error.type.toLocaleLowerCase()}`;
      const translatedMessage = this.#i18nService.t(errorKey);

      return translatedMessage === errorKey ? error.message : translatedMessage;
    })();

    const toast = this.#toastService.error(message, {
      action: {
        text: this.#i18nService.t('actions.retry'),
        callback: () => {
          this.#toastService.remove(toast.id);
          this.submit();
        },
      },
    });
  }

  #clearState(): void {
    this.#formGroup.markAsPristine();
    this.#formGroup.markAsUntouched();
    this.submited.set(false);
  }
}
